import { useTranslation } from 'react-i18next';

import { toaster } from '@kp/react-ui';

import { useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';

import { getTemplateLanguage, sendMessage } from '@/api/message';
import { getUsers } from '@/api/user';
import type { EmailFormData } from '@/components/EmailForm';
import { useTenantName } from '@/hooks/useTenantName';

import { LEARN_MORE_RECIPIENT_ROLES } from '../constants';

// ponytail: single page of recipients; paginate if it ever exceeds it
const RECIPIENTS_LIMIT = 100;

const TOAST_DISMISS_TIMEOUT = 3000;

interface UseLearnMoreRequestOptions {
  onSuccess: () => void;
}

/**
 * Send a "Learn more" request about an app to the tenant's contract managers,
 * sustainability officers and sales representatives, with the customer
 * contact template.
 */
export const useLearnMoreRequest = ({
  onSuccess,
}: UseLearnMoreRequestOptions) => {
  const { t, i18n } = useTranslation();
  const { name: tenantName = '' } = useTenantName();

  const { mutate, isPending } = useMutation({
    mutationFn: async (form: EmailFormData) => {
      const { data: recipients = [] } = await getUsers({
        roles: LEARN_MORE_RECIPIENT_ROLES,
        limit: RECIPIENTS_LIMIT,
      });
      const emails = [
        ...new Set(recipients.map(({ email }) => email).filter(Boolean)),
      ];

      if (emails.length === 0) {
        throw new Error('No recipients found for the learn more request');
      }

      return sendMessage({
        template_name: `insights_customer_contact_${getTemplateLanguage(i18n.language)}`,
        subscriber_emails: emails,
        data: {
          senderName: form.fullName,
          senderEmail: form.email,
          subject: form.subject,
          message: form.message,
          tenantName,
          dateSent: format(new Date(), 'dd.MM.yyyy, HH:mm'),
        },
      });
    },
    onSuccess: () => {
      toaster.add({
        type: 'default',
        id: 'learnMoreRequestSuccess',
        content: t('emailForm.response.success'),
        dismissTimeout: TOAST_DISMISS_TIMEOUT,
      });
      onSuccess();
    },
    onError: () => {
      toaster.add({
        type: 'error',
        id: 'learnMoreRequestError',
        content: t('emailForm.response.error'),
        dismissTimeout: TOAST_DISMISS_TIMEOUT,
      });
    },
  });

  return { sendLearnMoreRequest: mutate, isSending: isPending };
};
